import { Box, Collapse, Flex } from "@chakra-ui/react";
import { motion } from "framer-motion";
import { ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import {
  MapIcon,
  RanaButton,
  RanaHeading,
  RanaLoading,
  RanaSelect,
  RanaTable,
} from "@nens/ranalib";

import { useSetCatalogueSectionChildState } from "@/lib/catalogue/components/catalogue-section";
import { SchematisationRevisionHistory } from "@/lib/schematisation-revision-history";

import { TenantSchematisationHelpBanner } from "./components/no-schematisations-banner";
import { CatalogueSchematisationDetails } from "./features/detail/catalogue-schematisation-details";
import { SchematisationList } from "./features/list/schematisation-list";
import { SchematisationsMap } from "./features/map/schematisations-map";
import { useSchematisationsOverview } from "./hooks/use-schematisations-overview";
import { AddToProjectButtonOverride } from "./types/add-to-project-button-override";

type SchematisationsOverviewProps = {
  disableSettingSearchParams?: boolean;
  isSectionExpanded?: boolean;
  overwriteAddToProjectButton?: AddToProjectButtonOverride;
};

export function SchematisationsOverview({
  disableSettingSearchParams = false,
  isSectionExpanded = true,
  overwriteAddToProjectButton,
}: SchematisationsOverviewProps) {
  const intl = useIntl();

  const {
    selectedTag,
    setSelectedTag,
    availableTags,
    schematisations,
    schematisationItemsOnMap,
    isSchematisationsLoading,
    filterMap,
    setFilterMap,
    schematisation,
    setSchematisation,
    schematisationLoading,
    shouldShowRevisionHistory,
    setViewRevisionHistory,
    searchString,
    setSearchString,
    onMapMoveEnd,
    setSelectedSchematisationId,
    showingMap,
    setShowingMap,
    selectedRevisionId,
    setSelectedRevisionId,
    tenantHasSchematisations,
    tenantHasSchematisationsLoading,
  } = useSchematisationsOverview({ disableSettingSearchParams, isSectionExpanded });

  const hasBackButton = shouldShowRevisionHistory || !!schematisation;

  useSetCatalogueSectionChildState({
    hasBackButton,
    hasStickyElement: !!schematisation,
    hasExpandedState: !!tenantHasSchematisations,
  });

  if (schematisationLoading || tenantHasSchematisationsLoading) {
    return <RanaLoading h="full" isCentered />;
  }

  if (shouldShowRevisionHistory && schematisation?.id !== undefined) {
    return (
      <SchematisationRevisionHistory
        setSelectedRevision={setSelectedRevisionId}
        schematisationId={schematisation.id}
        backLabel={
          <FormattedMessage
            id="button.back_to_schematisation"
            defaultMessage="Model schematisation"
          />
        }
        back={() => setViewRevisionHistory(false)}
      />
    );
  }

  if (schematisation) {
    return (
      <CatalogueSchematisationDetails
        schematisation={schematisation}
        backToSchematisations={() => setSchematisation(undefined)}
        showRevisionHistory={() => setViewRevisionHistory(true)}
        selectedRevisionId={selectedRevisionId}
        overwriteAddToProjectButton={overwriteAddToProjectButton}
      />
    );
  }

  if (!tenantHasSchematisations) {
    return (
      <SchematisationListWrapper schematisationsAmount={0}>
        <TenantSchematisationHelpBanner />
      </SchematisationListWrapper>
    );
  }

  return (
    <SchematisationListWrapper
      schematisationsAmount={schematisations.length}
      headerButton={
        <RanaButton
          variant="secondary"
          leftIcon={<MapIcon />}
          onClick={() => setShowingMap(!showingMap)}
          opacity={isSectionExpanded ? 1 : 0}
          pointerEvents={isSectionExpanded ? "all" : "none"}
          transition="opacity 0.2s ease-in-out"
        >
          {showingMap ? (
            <FormattedMessage id="button.hide_map" defaultMessage="Hide map" />
          ) : (
            <FormattedMessage id="button.show_map" defaultMessage="Show map" />
          )}
        </RanaButton>
      }
    >
      <Collapse in={showingMap} unmountOnExit>
        <SchematisationsMap
          schematisationItemsOnMap={schematisationItemsOnMap}
          isSchematisationsLoading={isSchematisationsLoading}
          filterMap={filterMap}
          setFilterMap={setFilterMap}
          onMapMoveEnd={onMapMoveEnd}
          setSelectedSchematisationId={setSelectedSchematisationId}
        />
      </Collapse>

      <Flex gap={4} justifyContent="space-between" mt={4}>
        <RanaTable.FilterComponents.Search
          placeholder={intl.formatMessage({
            id: "form.search",
            defaultMessage: "Search",
          })}
          searchString={searchString}
          setSearchString={setSearchString}
        />

        <RanaSelect
          w="xs"
          value={selectedTag}
          onChange={(value) => setSelectedTag(value)}
          options={[
            {
              value: null,
              label: intl.formatMessage({
                id: "schematisations.filter_by_tag.all",
                defaultMessage: "All tags",
              }),
            },
            ...availableTags.map((tag) => ({
              value: tag,
              label: tag,
            })),
          ]}
        />
      </Flex>

      <Box mt={2}>
        <SchematisationList
          isSectionExpanded={isSectionExpanded}
          schematisations={schematisations}
          isSchematisationsLoading={isSchematisationsLoading}
          setSchematisation={setSchematisation}
        />
      </Box>
    </SchematisationListWrapper>
  );
}

type SchematisationListWrapperProps = {
  children: ReactNode;
  headerButton?: ReactNode;
  schematisationsAmount: number;
};

const SchematisationListWrapper = ({
  children,
  headerButton,
  schematisationsAmount,
}: SchematisationListWrapperProps) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1, ease: "easeOut" }}
    >
      <Flex flexGrow={1} position="relative" flexDirection="column">
        <Flex
          pointerEvents="none"
          sx={{ "*": { pointerEvents: "all" } }}
          position="relative"
          zIndex={3}
          flexWrap="wrap"
          pb={4}
          gap={2}
          justifyContent="space-between"
        >
          <Flex>
            <RanaHeading type="h2">
              <FormattedMessage
                id="heading.model_schematisation"
                defaultMessage="Model Schematisations"
              />
            </RanaHeading>

            {schematisationsAmount > 0 && (
              <Flex ml={2} mt={1} color="brand.grey.500">
                {`(${schematisationsAmount})`}
              </Flex>
            )}
          </Flex>

          {headerButton}
        </Flex>

        {children}
      </Flex>
    </motion.div>
  );
};
